// components/orders/AudioGiftModal.jsx
import React, { useState, useEffect } from 'react';
import { CloseIcon } from '../../public/icons';
import { Time, Teddy } from '../../public/images/shop';
import { MusicDropIcon, MicIcon } from '../../public/icons';
import reviewService from '../../services/reviewService';
import AudioRecorder from '../general/AudioRecorder';
import orderService from '../../services/ordersRouteServce';

const AudioGiftModal = ({
  isOpen,
  onClose,
  orderId,
  giftId,
  gift,
  orderNumber,
  onSaved
}) => {
  /* ── Gift item selection ── */
  const [selectedSet, setSelectedSet] = useState('');
  const [giftItemLabel, setGiftItemLabel] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [personalNote, setPersonalNote] = useState('');
  const [audioUrl, setAudioUrl] = useState('');

  /* ── Audio upload ── */
  const [audioFile, setAudioFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  /* ── Recorder ── */
  const [showRecorder, setShowRecorder] = useState(false);

  /* ── Save state ── */
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  /* ── Reset on open ── */
  useEffect(() => {
    if (!isOpen) return;
    setSaveError('');
    setUploadError('');
  }, [isOpen]);

  /* ── Load previously saved content ── */
  useEffect(() => {
    if (!isOpen || !orderId || !giftId) return;

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      try {
        const res = await orderService.getFutureGiftContent(orderId, giftId);
        if (cancelled) return;
        const data = res?.data;
        if (!data) return;

        if (data.giftItem) setSelectedSet(data.giftItem);
        if (data.giftItemLabel) setGiftItemLabel(data.giftItemLabel);
        if (data.color) setSelectedColor(data.color);
        if (data.personalizedMessage) setPersonalNote(data.personalizedMessage);
        if (data.audioFile?.url) setAudioFile(data.audioFile);
        if (data.audioUrl) setAudioUrl(data.audioUrl);
      } catch (err) {
        console.error('Failed to load audio gift content:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, orderId, giftId]);

  if (!isOpen) return null;

  /* ── Audio upload ── */
  const handleAudioUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError('');

    if (!file.type.startsWith('audio/')) {
      setUploadError('Please select an audio file (MP3, WAV, M4A).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Audio must be under 10MB.');
      return;
    }

    setIsUploading(true);
    try {
      const result = await reviewService.uploadAudioToCloudinary(file);
      setAudioFile({
        url: result.url,
        publicId: result.publicId,
        name: file.name,
        duration: result.duration || null
      });
    } catch (err) {
      console.error('Audio upload failed:', err);
      setUploadError('Failed to upload audio. Please try again.');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveAudio = () => {
    setAudioFile(null);
    setUploadError('');
  };

  const handleRecordingSaved = (recording) => {
    setAudioFile({
      url: recording.url,
      publicId: recording.publicId,
      name: recording.name,
      duration: recording.duration || null
    });
    setShowRecorder(false);
    setUploadError('');
  };

  /* ── Save to server ── */
  const handleSave = async () => {
    setSaveError('');

    if (!orderId || !giftId) {
      setSaveError('Missing order or gift reference.');
      return;
    }

    setIsSaving(true);
    try {
      await orderService.saveFutureGiftContent(orderId, giftId, {
        /* Audio-gift-specific fields, stored under FutureGiftContent */
        giftItem: selectedSet,
        giftItemLabel,
        color: selectedColor,
        personalizedMessage: personalNote.trim(),
        audioFile: audioFile || null,
        audioUrl: audioUrl.trim(),
        audioSource: audioFile
          ? 'upload'
          : audioUrl.trim()
          ? 'url'
          : null,
        revealDate: gift?.deliveryDate || null,
        revealTime: ''
      });

      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('Save audio gift failed:', err);
      setSaveError(
        err?.response?.data?.message ||
          err?.message ||
          'Could not save. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const isSaveEnabled =
    selectedSet && !isUploading && !isSaving;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300"
        onClick={() => {
          if (!isSaving) onClose();
        }}
      />

      {/* Modal */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] lg:w-[50%] xl:w-[40%] bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between mx-6 mb-4 mt-6 pb-2 border-b-2 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">
                Create your Audio Gifts
              </h2>
              <p className="text-[#6B635D] text-sm mt-1">
                Choose how your voice should be delivered
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={isSaving}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded disabled:opacity-50"
            >
              <CloseIcon className="w-6 h-6 text-[#1C2A46]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Choose Audio Gift Item */}
            <div className="mb-8">
              <h3 className="text-lg text-left font-medium text-[#1C2A46] mb-4">
                Choose Audio Gift Item*
              </h3>
              <div className="flex gap-4">
                {/* Teddy Bear */}
                <button
                  onClick={() => {
                    setSelectedSet('teddy-bear');
                    setGiftItemLabel('Teddy Bear');
                  }}
                  className={`flex-1 pt-1 px-1 border-2 text-center transition-colors ${
                    selectedSet === 'teddy-bear'
                      ? 'border-[#55000C] '
                      : 'border-[#E4E4E4] hover:border-[#55000C]'
                  }`}
                >
                  <img src={Teddy} alt="Teddy Bear" />
                  <div className="flex justify-between items-center mt-3">
                    <div className="text-base font-normal text-[#1C2A46] mb-1">
                      Teddy Bear
                    </div>
                    <div className="text-[#389B51] font-normal">+₦20,000</div>
                  </div>
                </button>

                {/* Wooden Alarm Clock */}
                <button
                  onClick={() => {
                    setSelectedSet('wooden-clock');
                    setGiftItemLabel('Wooden Alarm Clock');
                  }}
                  className={`flex-1 pt-1 px-1 border-2 text-center transition-colors ${
                    selectedSet === 'wooden-clock'
                      ? 'border-[#55000C] '
                      : 'border-[#E4E4E4] hover:border-[#55000C]'
                  }`}
                >
                  <img src={Time} alt="Wooden Alarm clock" />
                  <div className="flex justify-between items-center mt-3">
                    <div className="text-base font-normal text-[#1C2A46] mb-1">
                      Wooden Alarm clock
                    </div>
                    <div className="text-[#389B51] font-normal">+₦20,000</div>
                  </div>
                </button>
              </div>
            </div>

            <div className="space-y-8 text-left">
              {/* Add Audio Content */}
              <div>
                <h4 className="text-lg font-medium text-[#1C2A46]">
                  Add Audio Content
                </h4>
                <p className="text-[#6B635D] font-light text-sm mb-3">
                  Upload or record audio files that will play from the selected
                  gift item.
                </p>

                {/* Upload audio message */}
                <div className="mb-4">
                  <label
                    htmlFor="audio-upload-input"
                    className={`block border-2 border-dashed p-8 text-center transition-colors ${
                      isUploading
                        ? 'border-[#D2A146] cursor-wait'
                        : 'border-[#E4E4E4] cursor-pointer hover:border-[#D2A146]'
                    }`}
                  >
                    <input
                      id="audio-upload-input"
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      onChange={handleAudioUpload}
                      disabled={isUploading}
                    />

                    {isUploading ? (
                      <>
                        <div className="w-20 h-10 bg-[#F5F5F5] rounded-full flex items-center justify-center mx-auto mb-3">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D2A146]" />
                        </div>
                        <div className="font-medium text-[#1C2A46] mb-1">
                          Uploading…
                        </div>
                      </>
                    ) : audioFile ? (
                      <>
                        <div className="w-20 h-10 bg-[#E7F4ED] rounded-full flex items-center justify-center mx-auto mb-3">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#2F8F5B"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </div>
                        <div className="font-medium text-[#1C2A46] mb-1 truncate max-w-[280px] mx-auto">
                          {audioFile.name}
                        </div>
                        <div className="text-sm text-[#6B635D] mb-3">
                          Audio uploaded successfully
                        </div>
                        <audio
                          src={audioFile.url}
                          controls
                          className="max-w-[260px] mx-auto"
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleRemoveAudio();
                          }}
                          className="mt-3 text-xs text-red-600 hover:underline"
                        >
                          Remove audio
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="w-20 h-10 bg-[#F5F5F5] rounded-full flex items-center justify-center mx-auto mb-3">
                          <MusicDropIcon />
                          <MicIcon />
                        </div>
                        <div className="font-medium text-[#1C2A46] mb-1">
                          Record or upload audio message
                        </div>
                        <div className="text-sm text-[#6B635D]">
                          MP3 (max. 10mb)
                        </div>
                      </>
                    )}
                  </label>

                  {uploadError && (
                    <p className="mt-2 text-xs text-red-600 text-left">
                      {uploadError}
                    </p>
                  )}
                </div>

                {/* Record Audio Instead */}
                {!showRecorder && !audioFile && (
                  <div className="mb-4 text-left">
                    <button
                      type="button"
                      onClick={() => setShowRecorder(true)}
                      className="text-sm text-[#55000C] underline hover:text-[#6a0a17] transition-colors"
                    >
                      Record Audio Instead
                    </button>
                  </div>
                )}

                {/* Inline recorder */}
                {showRecorder && !audioFile && (
                  <div className="mb-6">
                    <AudioRecorder
                      onRecorded={handleRecordingSaved}
                      onCancel={() => setShowRecorder(false)}
                    />
                  </div>
                )}

                {/* Audio URL */}
                <div className="mb-6">
                  <label className="block text-base font-normal text-[#1C2A46] mb-2">
                    Audio Url (optional)
                  </label>
                  <input
                    type="text"
                    value={audioUrl}
                    onChange={(e) => setAudioUrl(e.target.value)}
                    placeholder="www.spotify/music/superpowers-by-Adele"
                    className="w-full px-4 py-4 border border-[#E4E4E4] bg-[#F5F5F5] text-[#6B635D] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#6B635D] placeholder:font-light placeholder:text-sm"
                  />
                </div>

                {saveError && (
                  <p className="text-sm text-red-600 mt-4">{saveError}</p>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6">
            <button
              onClick={handleSave}
              disabled={!isSaveEnabled}
              className={`w-full py-3 font-medium transition-colors ${
                isSaveEnabled
                  ? 'bg-[#D2A146] text-white hover:bg-[#C49660]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSaving
                ? 'Saving…'
                : isUploading
                ? 'Uploading…'
                : 'Save Content'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AudioGiftModal;